import React from 'react';
import styled, { css } from 'styled-components';

type ExpandablePanelProps = {
  isExpanded: boolean;
  /**
   * Presents the panel as a floating card instead of a full-height wall
   * (PLAT-11087): inset from the workspace edges, rounded on every corner and
   * only as tall as its content, with a gap to the rail rather than a shared
   * seam. The card's own chrome comes from `FloatingToolPanel`; this flag owns
   * the box it sits in.
   */
  isFloating?: boolean;
  /**
   * PLAT-12096 — a press on the floating panel's empty column (under and
   * beside the card, not on the card) — the same as a press on the empty
   * stage.
   */
  onEmptyPress?: () => void;
  children: React.ReactNode;
};

const PANEL_WIDTH = 350; // Width of the expanded panel content area

/**
 * Gap between the card and each of its horizontal neighbours — the workspace on
 * the left, the rail on the right. Small enough on the rail side that the card
 * still reads as belonging to the nav item that opened it.
 */
const FLOATING_GAP_X = 8;
/** Inset from the top and bottom of the workspace. */
const FLOATING_INSET_Y = 16;
/**
 * In-flow width the open panel takes from the stage — the card is only
 * visually floating, its column is real. Layout math elsewhere reads this.
 */
export const EXPANDED_PANEL_COLUMN_WIDTH = PANEL_WIDTH + FLOATING_GAP_X * 2;

export const ExpandablePanel: React.FC<ExpandablePanelProps> = ({
  isExpanded,
  isFloating = false,
  onEmptyPress,
  children,
}) => {
  return (
    <PanelContainer
      isExpanded={isExpanded}
      isFloating={isFloating}
      onMouseDown={e => {
        if (isFloating && e.target === e.currentTarget) onEmptyPress?.();
      }}
    >
      <PanelContent isExpanded={isExpanded} isFloating={isFloating}>
        {children}
      </PanelContent>
    </PanelContainer>
  );
};

const PanelContainer = styled.div<{ isExpanded: boolean; isFloating: boolean }>`
  position: relative;
  height: 100%;
  transition: width var(--transition-sidebar) cubic-bezier(0.4, 0, 0.2, 1);

  ${props =>
    props.isFloating
      ? css`
          /* Reserve the card plus a gap on either side, so the card floats in
             its own space rather than covering the canvas or the timeline. */
          width: ${props.isExpanded ? EXPANDED_PANEL_COLUMN_WIDTH : 0}px;
          /* Let the card's shadow escape the reserved column. The collapse is
             carried by the fade below, which finishes first. */
          overflow: visible;
        `
      : css`
          overflow: hidden;
          width: ${props.isExpanded ? `${PANEL_WIDTH}px` : '0'};
          border-left: ${props.isExpanded ? '1px solid var(--border)' : 'none'};
        `}
`;

const PanelContent = styled.div<{ isExpanded: boolean; isFloating: boolean }>`
  position: absolute;
  right: 0;
  top: 0;
  width: ${PANEL_WIDTH}px;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: var(--background);
  transition:
    opacity var(--transition-normal) ease,
    transform var(--transition-sidebar) cubic-bezier(0.4, 0, 0.2, 1);

  ${props =>
    props.isFloating &&
    css`
      right: ${FLOATING_GAP_X}px;
      top: ${FLOATING_INSET_Y}px;
      /* Height follows the content, capped by the space between the insets —
         so a short tool yields a short card and a long one scrolls inside
         itself. The card supplies the surface; this box is just its frame. */
      height: auto;
      max-height: calc(100% - ${FLOATING_INSET_Y * 2}px);
      background-color: transparent;
    `}

  ${props =>
    props.isExpanded
      ? css`
          opacity: 1;
        `
      : css`
          opacity: 0;
          transform: translateX(20px);
          pointer-events: none;
        `}
`;

export default ExpandablePanel;
